import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismIcon,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, or } from 'ember-truth-helpers';
import PaymentModalPayBalance from 'frontend-payments/components/payment/modal-pay-balance';
import PaymentModalRecurringPaymentMethod from 'frontend-payments/components/payment/modal-recurring-payment-method';
import type { Account, Transaction } from 'frontend-payments/models';
import type AjaxService from 'frontend-payments/services/ajax';

import styles from './payment-details.module.css';

const PAYMENT_METHOD_STORAGE_KEY_PREFIX = 'pending-payment-method';

// 5 days in milliseconds
const PENDING_PAYMENT_METHOD_EXPIRY_MS = 5 * 24 * 60 * 60 * 1000;

interface PendingPaymentMethodData {
  timestamp: number;
}

interface PaymentPaymentDetailsSignature {
  Args: {
    account: Account | null;
    isAccountRequestLoading: boolean;
    isTransactionRequestLoading: boolean;
    transactions: Transaction[];
  };
}

export default class PaymentPaymentDetails extends Component<PaymentPaymentDetailsSignature> {
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare session: Services['session'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked cachedAccountNumber: string | null = null;
  @tracked cachedPaymentMethodData: PendingPaymentMethodData | null = null;

  @tracked isDeleteModalOpen = false;
  @tracked isModalPayBalanceOpen = false;
  @tracked isModalRecurringPaymentMethodOpen = false;

  deletePaymentMethodTask = task(async (): Promise<void> => {
    if (!this.args.account?.accountNumber) {
      return;
    }

    const paymentMethodId =
      this.args.account.payments.recurringPaymentMethod?.id;

    if (!paymentMethodId) {
      return;
    }

    try {
      // Since payment methods are embedded data, we'll use a custom DELETE request
      const url = `/api/v4/accounts/${this.args.account.accountNumber}/payment-methods/${paymentMethodId}`;

      await this.ajax.delete(url, {});

      // Show success toast
      this.toast.success(
        this.intl.t('payments.recurring_payment.recurring_payment_deleted'),
        {
          showIcon: true,
        },
      );

      this.usageAnalytics.track('Payment Method Removed', {
        eventTrigger: 'payment-method-modal',
      });

      this.isDeleteModalOpen = false;

      // Clear any cached payment method data
      this.clearPendingPaymentMethod();

      // Reload the account model to ensure we have the latest data from the server
      if (this.args.account.id) {
        await this.store.findRecord('account', this.args.account.id, {
          reload: true,
        });
      }
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
      this.isDeleteModalOpen = false;
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get balanceClass() {
    if (this.args.isAccountRequestLoading || !this.args.account?.balance) {
      return '';
    }

    const amount = parseFloat(this.args.account.balance);

    if (amount === 0) {
      return 'text-default';
    }

    return amount > 0 ? 'text-critical-default' : 'text-success-default';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedBalance() {
    if (this.args.isAccountRequestLoading || !this.args.account) {
      return null;
    }

    const amount = parseFloat(this.args.account.balance);

    return this.intl.formatNumber(amount, {
      style: 'currency',
      currency: this.args.account.currency,
    });
  }

  get isProcessingPaymentMethod(): boolean {
    if (this.args.account?.payments.recurringPaymentMethod) {
      // Clear pending state if we now have an active payment method
      this.clearPendingPaymentMethod();
      return false;
    }

    return this.pendingPaymentMethodData !== null;
  }

  get pendingPaymentData(): PendingPaymentMethodData | null {
    if (!this.args.account?.accountNumber) {
      return null;
    }

    const accountNumber = this.args.account.accountNumber;
    const isCached = this.cachedAccountNumber === accountNumber;
    return isCached
      ? this.cachedPaymentMethodData
      : this.loadFromLocalStorage(accountNumber);
  }

  get pendingPaymentMethodData(): PendingPaymentMethodData | null {
    const data = this.pendingPaymentData;
    if (!data || this.isPaymentMethodDataExpired(data)) {
      return null;
    }
    return data;
  }

  clearPendingPaymentMethod(): void {
    if (!this.args.account?.accountNumber) {
      return;
    }

    const accountNumber = this.args.account.accountNumber;
    const storageKey = this.getStorageKey(accountNumber);

    // Remove from localStorage and clear cache for reactivity
    this.localStorage.removeItem(storageKey);

    // Only clear cache if it's for the current account
    if (this.cachedAccountNumber === accountNumber) {
      this.cachedPaymentMethodData = null;
    }
  }

  getStorageKey(accountNumber: string): string {
    return `${PAYMENT_METHOD_STORAGE_KEY_PREFIX}-${accountNumber}`;
  }

  isPaymentMethodDataExpired(data: PendingPaymentMethodData): boolean {
    const timeSinceTimestamp = dayjs().diff(dayjs(data.timestamp));
    if (timeSinceTimestamp > PENDING_PAYMENT_METHOD_EXPIRY_MS) {
      return true;
    }
    return false;
  }

  loadFromLocalStorage(accountNumber: string): PendingPaymentMethodData | null {
    try {
      const storageKey = this.getStorageKey(accountNumber);
      const storedData = this.localStorage.getItem(storageKey);
      if (!storedData) {
        return null;
      }

      return JSON.parse(storedData) as PendingPaymentMethodData;
    } catch {
      return null;
    }
  }

  @action
  cancelDeletePaymentMethod(): void {
    this.isDeleteModalOpen = false;
  }

  @action
  closeModalPayBalance(): void {
    this.isModalPayBalanceOpen = false;
  }

  @action
  closeModalRecurringPaymentMethod(): void {
    this.isModalRecurringPaymentMethodOpen = false;
  }

  @action
  async deletePaymentMethod(): Promise<void> {
    const shouldDelete = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('payments.recurring_payment.delete_modal.title'),
      text: this.intl.t('payments.recurring_payment.delete_modal.description'),
      buttons: {
        cancel: { text: this.intl.t('general.cancel') },
        confirm: {
          text: this.intl.t(
            'payments.recurring_payment.delete_modal.delete_method',
          ),
        },
      },
    });

    if (!shouldDelete) {
      return;
    }

    await this.deletePaymentMethodTask.perform();
  }

  @action
  onPaymentMethodSaved(): void {
    this.closeModalRecurringPaymentMethod();

    if (!this.args.account?.accountNumber) {
      return;
    }

    const accountNumber = this.args.account.accountNumber;
    const timestamp = dayjs().valueOf();
    const data: PendingPaymentMethodData = {
      timestamp,
    };

    // Store in localStorage and update cache for reactivity
    const storageKey = this.getStorageKey(accountNumber);
    this.localStorage.setItem(storageKey, JSON.stringify(data));
    this.cachedAccountNumber = accountNumber;
    this.cachedPaymentMethodData = data;
  }

  @action
  openModalPayBalance(): void {
    this.isModalPayBalanceOpen = true;
  }

  @action
  openModalRecurringPaymentMethod(): void {
    this.isModalRecurringPaymentMethodOpen = true;
  }

  <template>
    <PrismCard>
      <:content>
        <div class="flex justify-between gap-400 pt-400">
          <div class={{styles.item}}>
            <p class={{styles.label}}>
              {{t "payments.payment_details.billed"}}
            </p>
            <p
              class={{concat
                styles.value
                " "
                (if
                  @isAccountRequestLoading
                  (concat styles.loading-pulse " " "w-1/2")
                )
              }}
            >
              {{@account.formattedFrequency}}
            </p>
          </div>

          <div class={{styles.item}}>
            <div class="flex gap-100">
              <p class={{styles.label}}>
                {{t "payments.payment_details.total_balance"}}
              </p>
              <div>
                <PrismIcon @name="circle-information-outline" @size="small" />
                <PrismTooltip
                  @side="top"
                  @text={{t "payments.payment_details.total_balance_tooltip"}}
                />
              </div>
            </div>
            <p
              class={{concat
                styles.value
                " "
                "mb-200"
                " "
                this.balanceClass
                " "
                (if
                  @isAccountRequestLoading
                  (concat styles.loading-pulse " " "w-1/2")
                )
              }}
            >
              {{this.formattedBalance}}
            </p>
            {{! Hide the pay balance button for now, as the backend functionality is not ready yet. }}
            {{!--
      <div class="flex">
        <Prism::Button
          @buttonType="secondary"
          @isDisabled={{@isTransactionRequestLoading}}
          @isLoading={{@isTransactionRequestLoading}}
          @label={{t "payments.pay_balance"}}
          @onClick={{this.openModalPayBalance}}
          @size="small"
        />
      </div>
      --}}
          </div>

          <div class={{styles.item}}>
            <p class={{styles.label}}>
              {{t "payments.recurring_payment"}}
            </p>
            {{#if this.isProcessingPaymentMethod}}
              <div class="mb-200 flex items-center gap-100">
                <p class={{styles.value}}>
                  {{t "payments.processing"}}
                </p>
                <div>
                  <PrismIcon @name="circle-information-outline" @size="small" />
                  <PrismTooltip
                    @side="top"
                    @text={{t "payments.methods.title.awaiting_authorization"}}
                  />
                </div>
              </div>
            {{else}}
              <p
                class={{concat
                  styles.value
                  " "
                  "mb-200"
                  " "
                  (if
                    @isAccountRequestLoading
                    (concat styles.loading-pulse " " "w-1/2")
                  )
                }}
              >
                {{@account.formattedActivePaymentMethod}}
                {{#if @account.payments.recurringPaymentMethod}}
                  <span class="text-200--regular">
                    {{@account.formattedLastFourDigits}}
                  </span>
                {{/if}}
              </p>
            {{/if}}
            <div class="flex">
              {{#unless this.isProcessingPaymentMethod}}
                {{#if @account.payments.recurringPaymentMethod}}
                  {{! Show delete button when there's an active payment method and not processing }}
                  <PrismButton
                    @buttonType="critical-secondary"
                    @isDisabled={{this.session.isStaffUser}}
                    @label="Delete"
                    @onClick={{this.deletePaymentMethod}}
                    @size="small"
                  />
                {{else}}
                  {{! Show add button when there's no active payment method (including processing state) }}
                  <PrismButton
                    @buttonType="secondary"
                    @iconBefore="plus"
                    @isDisabled={{or
                      @isAccountRequestLoading
                      this.session.isStaffUser
                    }}
                    @label={{t "payments.recurring_method.recurring_payment"}}
                    @onClick={{this.openModalRecurringPaymentMethod}}
                    @size="small"
                  />
                {{/if}}
              {{/unless}}
            </div>
          </div>
        </div>
      </:content>
    </PrismCard>

    {{#if this.isModalPayBalanceOpen}}
      <PaymentModalPayBalance
        @onCloseModal={{this.closeModalPayBalance}}
        @transactions={{@transactions}}
      />
    {{/if}}

    {{#if (and this.isModalRecurringPaymentMethodOpen @account)}}
      <PaymentModalRecurringPaymentMethod
        {{! @glint-expect-error: Incorrect type }}
        @account={{@account}}
        @onCloseModal={{this.closeModalRecurringPaymentMethod}}
        @onPaymentMethodSaved={{this.onPaymentMethodSaved}}
      />
    {{/if}}
  </template>
}
